import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricDetailsExploreGoalsItemComponent from 'webapp/components/metric-details/explore/goals/item';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { MetricSetup } from 'webapp/models-custom';

interface Args {
	metricSetup: MetricSetup;
	origin: string;
}

export default class MetricDetailsGoalsComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		void this.mmGoalsService.requestSpaceGoalsData();
	}

	// the mobile drawer can't create a goal, so it has nothing to show without one
	get isReadOnly(): boolean {
		return this.metricDetailsService.isMobileMode;
	}

	get showGoals(): boolean {
		if (!this.args.metricSetup.metric || !this.billingService.goalsEnabled) {
			return false;
		}
		return !this.isReadOnly || !!this.goals.length;
	}

	@cached
	get goals() {
		return this.mmGoalsService.getValidGoalsForMetricSetup(this.args.metricSetup);
	}

	@action
	createGoal(): void {
		this.mmGoalsService.openNewPopupForMetricSetup(this.args.metricSetup, this.args.origin);
	}

	<template>
		{{#if this.showGoals}}
			<div class="module-box metric-explorer-goals">
				{{#if this.goals.length}}
					<div class="module-box__header">
						<div class="module-box__title">
							<CommonUiIconComponent @name="OkRs" @size="md" />
							<span>Aligned goals</span>
						</div>
						{{#unless this.isReadOnly}}
							<SubmitButtonComponent
								@theme="blue-outlined"
								@text="New goal"
								@icon="Plus"
								@onTrigger={{this.createGoal}}
							/>
						{{/unless}}
					</div>
					<div class="module-box__divider"></div>
					{{#each this.goals as |goal|}}
						<MetricDetailsExploreGoalsItemComponent @model={{goal}} />
					{{/each}}
				{{else}}
					<div class="metric-explorer-goals__empty">
						<CommonUiIconComponent @name="OkRs" @size="2xl" />
						<div class="metric-explorer-goals__empty-text">
							<span class="title">
								No goal is set for
								{{@metricSetup.metric.name}}
								yet
							</span>
							<span class="subtitle">
								Companies that set goals are more likely to increase their
								performance.
							</span>
						</div>
						<SubmitButtonComponent
							@theme="blue-outlined"
							@text="Set your goal"
							@icon="Plus"
							@onTrigger={{this.createGoal}}
						/>
					</div>
				{{/if}}
			</div>
		{{/if}}
	</template>
}
